/**
 * useBRRData Hook
 * Manages loading and state for BRR form data
 */

import { useState, useEffect, useCallback } from 'react';
import { BRRApiClient } from '../services';
import { BRRData } from '../services/types';

interface UseBRRDataOptions {
  batchId: string | null;
  apiClient: BRRApiClient;
  autoLoad?: boolean;
}

interface UseBRRDataReturn {
  data: BRRData | null;
  loading: boolean;
  error: Error | null;
  load: () => Promise<void>;
  reload: () => Promise<void>;
  updateData: (updater: (prev: BRRData) => BRRData) => void;
}

/**
 * Hook for loading and managing BRR data
 *
 * @example
 * const { data, loading, error, reload } = useBRRData({
 *   batchId: 'B-2026-0147',
 *   apiClient: brrApi,
 *   autoLoad: true
 * });
 */
export const useBRRData = ({
  batchId,
  apiClient,
  autoLoad = true
}: UseBRRDataOptions): UseBRRDataReturn => {
  const [data, setData] = useState<BRRData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    if (!batchId) {
      setError(new Error('No batch ID provided'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const brrData = await apiClient.getBRRData(batchId);
      setData(brrData);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      console.error('Failed to load BRR data:', error);
    } finally {
      setLoading(false);
    }
  }, [batchId, apiClient]);

  const reload = useCallback(async () => {
    await load();
  }, [load]);

  const updateData = useCallback((updater: (prev: BRRData) => BRRData) => {
    setData((prev) => {
      if (!prev) return prev;
      return updater(prev);
    });
  }, []);

  // Auto-load on mount or when batchId changes
  useEffect(() => {
    if (autoLoad && batchId) {
      void load();
    }
  }, [batchId, autoLoad, load]);

  return {
    data,
    loading,
    error,
    load,
    reload,
    updateData
  };
};
